// @flow
import styled from '@emotion/styled';
import {
  SPACING,
  color,
  COLORS,
  BORDER_RADIUS,
  typography
} from '@tablekit/theme';
import { variant, css } from '@tablekit/utils';
import GatsbyLink from 'components/GatsbyLink';
import { buildTruncationCss } from 'components/TruncatedText';
import Image from 'components/Image';
import PersonImage from 'components/PersonImage';
import BaseCard, { MEDIUM_SIZE, FULL_TOP_IMAGE_CLASS } from 'styled/Card';
import {
  DESKTOP_BREAKPOINT,
  LAPTOP_BREAKPOINT,
  MOBILE_BREAKPOINT
} from 'utils/theme';

export const CARD_FORMATS = {
  FULL: 'full',
  HALF: 'half',
  THIRD: 'third'
};

export const CardCategoryRow = styled.div`
  display: flex;
  margin-left: -${SPACING.L1};
  margin-bottom: ${SPACING.L2};
`;

export const CardCategory = styled.div`
  ${typography('L1')};
  display: inline-flex;
  margin: 0 ${SPACING.L1};
  color: ${color('text')};
  border: 1px solid ${COLORS.GRAY.L5};
  padding: ${SPACING.L1} ${SPACING.L2};
  &,
  &:active,
  &:hover {
    text-decoration: none;
  }
`;

export const GUTTER = parseInt(SPACING.L6, 10);
const CARD_BACKGROUND = 'white';

const COLUMNS = 6;
const SINGLE_COLUMN_WIDTH = MEDIUM_SIZE / 2 + GUTTER / 2;
const GUTTER_COLUMN_OFFSET = GUTTER / COLUMNS;

let columns = '';
for (let i = 0; i < COLUMNS; i += 1) {
  columns += ` ${SINGLE_COLUMN_WIDTH + GUTTER_COLUMN_OFFSET}px`;
}

export const GridOuter = styled.div`
  width: 100%;
  max-width: 100%;
  padding: ${SPACING.L4};

  @media ${DESKTOP_BREAKPOINT} {
    margin: 0 auto;
    width: ${SINGLE_COLUMN_WIDTH * COLUMNS}px;
    padding: 0;
  }
`;

const GridInner = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  width: 100%;
  @media ${DESKTOP_BREAKPOINT} {
    -ms-grid-columns: ${columns};
    grid-template-columns: ${columns};
  }
  @media ${LAPTOP_BREAKPOINT} {
    display: -ms-grid;
    display: grid;
    ${({ rows }) => {
      if (!rows) return '';
      let result = '';
      for (let i = 0; i < rows; i += 1) {
        result += '1fr ';
      }
      return `-ms-grid-rows: ${result}`;
    }};
    -ms-grid-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    margin: 0 -${GUTTER / 2}px;
    width: calc(100% + ${GUTTER}px);
  }
`;

export const Grid = ({ children, rows, ...props }: *) => (
  <GridOuter {...props}>
    <GridInner rows={rows}>{children}</GridInner>
  </GridOuter>
);

export const CardTitle = styled.h2`
  color: ${color('text')};
  ${buildTruncationCss('L5', 2, true)};
  margin-bottom: ${SPACING.L2};
`;

export const CardText = styled.p`
  color: ${color('text')};
  ${buildTruncationCss('L3', 3, true)};
  margin-bottom: ${SPACING.L2};
`;

export const CardAuthor = styled.p`
  color: ${color('primary')};
  ${typography('L2')};
`;

export const CardPublishedDate = styled(CardAuthor)`
  color: ${color('text')};
  ${typography('L1')};
`;

export const CardTextContent = styled.div`
  padding: ${SPACING.L4};
  flex: 1 1 auto;
  * > :last-child {
    margin-bottom: 0 !important;
  }
`;

export const CardPersonImage = styled(PersonImage)`
  margin-right: ${SPACING.L6};
`;

export const CardFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-top: auto;
`;

export const IMAGE_HEIGHTS = {
  [CARD_FORMATS.FULL]: 464,
  [CARD_FORMATS.HALF]: 238,
  [CARD_FORMATS.THIRD]: 165,
  mobile: 200
};

export const CardImage = styled(Image, {
  shouldForwardProp: (prop) => ['format'].indexOf(prop) === -1
})`
  object-fit: cover;
  overflow: hidden;
  & > * {
    // This forces the image to fill at all times (due to picture styles)
    min-width: 100% !important;
    min-height: 100%;
  }
  ${variant({
    prop: 'format',
    default: CARD_FORMATS.THIRD,
    variants: {
      [CARD_FORMATS.FULL]: css`
        width: 60%;
        min-width: 60%;
        height: ${IMAGE_HEIGHTS[CARD_FORMATS.FULL]}px;
      `,
      [CARD_FORMATS.HALF]: css`
        width: 100%;
        height: ${IMAGE_HEIGHTS[CARD_FORMATS.HALF]}px;
      `,
      [CARD_FORMATS.THIRD]: css`
        width: 100%;
        height: ${IMAGE_HEIGHTS[CARD_FORMATS.THIRD]}px;
      `
    }
  })};

  @media ${MOBILE_BREAKPOINT} {
    height: 200px;
    min-height: 200px;
    width: 100%;
  }
`;

export const CardPositioner = styled.div`
  position: relative;
  margin: 0 ${GUTTER / 2}px ${GUTTER}px;
  display: flex;
  width: auto;
  height: auto;

  ${variant({
    prop: 'format',
    default: CARD_FORMATS.THIRD,
    variants: {
      [CARD_FORMATS.FULL]: css`
        grid-column-end: span 6;
        flex-direction: row;
      `,
      [CARD_FORMATS.HALF]: css`
        grid-column-end: span 3;
        flex-direction: column;
      `,
      [CARD_FORMATS.THIRD]: css`
        grid-column-end: span 2;
        flex-direction: column;
      `
    }
  })};

  @media ${MOBILE_BREAKPOINT} {
    height: auto;
    min-height: ${IMAGE_HEIGHTS.mobile}px;
    width: 100% !important;
    margin-left: 0;
  }
`;

export const Card = styled(BaseCard.withComponent(GatsbyLink), {
  shouldForwardProp: (prop) =>
    ['format', 'showFooter', 'showPreview'].indexOf(prop) === -1
})`
  display: flex;
  flex: 1 1 auto;
  background: ${CARD_BACKGROUND};
  &,
  &:hover,
  &:active {
    text-decoration: none;
  }

  & > div {
    display: flex;
    flex-direction: column;
  }

  ${variant({
    prop: 'format',
    default: CARD_FORMATS.THIRD,
    variants: {
      [CARD_FORMATS.FULL]: css`
        flex-direction: row;

        ${CardTextContent} {
          flex: 1 1 100%;
        }

        ${CardTitle} {
          ${(props) =>
            props.showPreview
              ? buildTruncationCss('L6', props.showFooter ? 2 : 3, true)(props)
              : css`
                  ${typography('L6')(props)}
                  max-height: none;
                  height: auto;
                `};
          margin-bottom: ${SPACING.L2};
        }

        ${CardText} {
          ${buildTruncationCss('L4', 6, true)};
          margin-bottom: ${SPACING.L9};
        }

        ${CardAuthor}, ${CardPublishedDate} {
          ${typography('L4')};
        }

        & .${FULL_TOP_IMAGE_CLASS} {
          transform-origin: center right;
          border-top-left-radius: ${BORDER_RADIUS}px;
          border-bottom-left-radius: ${BORDER_RADIUS}px;
          border-top-right-radius: 0px;
          transition: transform 0.2s ease-in-out;
        }
      `,
      [CARD_FORMATS.HALF]: css`
        ${({ showPreview, showFooter }) =>
          !showPreview && !showFooter
            ? css`
                ${CardTitle} {
                  max-height: none;
                  height: auto;
                }
              `
            : ''}
      `,
      [CARD_FORMATS.THIRD]: css`
        ${({ showPreview, showFooter }) =>
          !showPreview && !showFooter
            ? css`
                ${CardTitle} {
                  max-height: none;
                  height: auto;
                }
              `
            : ''}
      `
    }
  })};

  @media ${MOBILE_BREAKPOINT} {
    margin: 0 0 ${SPACING.L6};
    flex-direction: column;

    ${CardTitle} {
      ${buildTruncationCss('L4', 2)};
      margin-bottom: ${SPACING.L2};
      height: auto;
    }
    ${CardText} {
      ${buildTruncationCss('L2', 2)};
      margin-bottom: ${SPACING.L2};
      height: auto;
    }

    ${CardAuthor}, ${CardPublishedDate} {
      ${typography('L1')};
    }
  }
`;
